import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, FormGroup, InputGroup, InputGroupItem, Modal, ModalVariant, SearchInput, Split, SplitItem, Stack, StackItem, Tab, Tabs, TabTitleIcon, TabTitleText, TextInput } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { uniq } from 'lodash-es';

import { selectContourModelsAndCustomizations, selectAllContourModelsAndCustomizations } from '../../../store/contouring/contouringSelectors';
import { ContourCustomizationSelection } from '../../../store/contouring/contouring-types';
import { activeFreeTextFilterSet, activeFreeTextFilterReset, activeTagFiltersReset, activeModalityFiltersReset, activeVersionTagFiltersReset, contouringSelectors } from '../../../store/contouring/contouringSlice';
import { capitalizeFirstCharacter } from '../../../util/string-convert';
import { getBodyPartIcon, BODY_PART_TAB_ALL } from '../../../components/body-part-icon';
import { BODY_PART_DISPLAY_ORDER } from '../../../store/global-types/customization-helpers';
import { filterCustomizationTreeByFreeText } from '../../../store/global-types/customizationSelectors';
import ModelList from '../../../components/model-list';
import { Model, CustomizationTreeViewDataItem, CustomizationObjectType, ModelType, ModalityDirection, ValidationStatus } from '../../../store/global-types/customization-types';
import CustomizationFilters from '../CustomizationFilters';

import './contour-model-select.css';

interface ContourModelSelectProps {
    onSelect: (action: string) => void,
    initialAction: string,
    model: Model,
}

const generateAction = (customization: ContourCustomizationSelection | undefined): string => {
    if (customization) {
        return `${customization.modelName}.${customization.customizationName}`;
    } else {
        return '';
    }
}

const getBodyPartTitle = (key: string, title: string) => {

    const icon = getBodyPartIcon(key);

    if (icon !== null) {
        return (<><TabTitleIcon>{icon}</TabTitleIcon> <TabTitleText>{title}</TabTitleText></>);
    }

    return (<TabTitleText>{title}</TabTitleText>);
}

// Helper function to convert ContourCustomizationSelection[] to CustomizationTreeViewDataItem[]
// This conversion is necessary to use the filterCustomizationTree utility which expects CustomizationTreeViewDataItem objects
const convertToTreeViewObjects = (customizations: ContourCustomizationSelection[]): CustomizationTreeViewDataItem[] => {
    return customizations.map(customization => ({
        id: customization.action,
        label: customization.customizationLabel,
        type: CustomizationObjectType.Model,
        children: [],
        parentId: undefined,
        isModified: false,
        validationStatus: ValidationStatus.Valid,
        isAvailable: customization.isAvailable,
        isDeprecated: customization.isDeprecated,
        isLatest: customization.isLatest || false,
        modelVisibility: customization.visibility,
        tags: customization.tags,
        version: customization.version,
        inputModalities: customization.inputModalities,
        outputModalities: customization.outputModalities,
    }));
};

// Helper function to convert filtered CustomizationTreeViewDataItem[] back to ContourCustomizationSelection[]
const convertFromTreeViewObjects = (tree: CustomizationTreeViewDataItem[], originalCustomizations: ContourCustomizationSelection[]): ContourCustomizationSelection[] => {
    const actionIds = tree.map(item => item.id);
    return originalCustomizations.filter(customization => actionIds.includes(customization.action));
};

const ContourModelSelect = (props: ContourModelSelectProps) => {

    const { initialAction, onSelect, model } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const contourCustomizations = useSelector(selectContourModelsAndCustomizations);
    const allContourCustomizations = useSelector(selectAllContourModelsAndCustomizations);
    const allContourModels = useSelector(contouringSelectors.selectModels);
    const filterValue = useSelector(contouringSelectors.selectActiveFreeTextFilter);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeBodyPartTab, setActiveBodyPartTab] = useState<string | number>(BODY_PART_TAB_ALL);
    const [selectedContourCustomization, setSelectedContourCustomization] = useState<ContourCustomizationSelection | undefined>(undefined);
    const [scrollToCustomization, setScrollToCustomization] = useState<ContourCustomizationSelection | undefined>(undefined);


    // Compute stable body part categories from all unfiltered models
    const stableBodyPartCategories = useMemo(() => {
        return uniq(allContourModels.map(model => model.bodyPart));
    }, [allContourModels]);

    // Track previous modal open state to detect when modal is first opened
    const prevIsModalOpenRef = React.useRef(isModalOpen);

    // initialize modal - only runs when modal transitions from closed to open
    useEffect(() => {
        const wasModalOpen = prevIsModalOpenRef.current;
        prevIsModalOpenRef.current = isModalOpen;

        // Only initialize when modal is newly opened (transition from false to true)
        if (isModalOpen && !wasModalOpen) {
            const initialCustomization = allContourCustomizations.find(c => c.action === initialAction);

            if (initialCustomization) {
                setSelectedContourCustomization(initialCustomization);
                setScrollToCustomization(initialCustomization);
                setActiveBodyPartTab(initialCustomization.bodyPart);
            } else {
                setSelectedContourCustomization(undefined);
                setScrollToCustomization(undefined);

                // if initial Contour+ model has not been set then default to opening the tab that matches
                // current Image+ model's body part
                setActiveBodyPartTab(model.bodyPart);
            }

            // Reset all contouring filters when modal opens to ensure a clean state
            // and prevent Contour+ page filter settings from carrying over
            dispatch(activeFreeTextFilterReset());
            dispatch(activeTagFiltersReset());
            dispatch(activeModalityFiltersReset(ModalityDirection.Input));
            dispatch(activeModalityFiltersReset(ModalityDirection.Output));
            dispatch(activeVersionTagFiltersReset());
        }
    }, [isModalOpen, allContourCustomizations, initialAction, model, dispatch]);

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleConfirm = useCallback(() => {
        handleCloseModal();
        onSelect(generateAction(selectedContourCustomization));
    }, [handleCloseModal, onSelect, selectedContourCustomization]);

    const handleBodyPartTabClick = useCallback((_: unknown, tab: string | number) => {
        setActiveBodyPartTab(tab);
    }, []);

    const handleSelect = useCallback((value: ContourCustomizationSelection) => {
        if (value === selectedContourCustomization) {
            setSelectedContourCustomization(undefined);
        } else {
            setSelectedContourCustomization(value);
        }

    }, [setSelectedContourCustomization, selectedContourCustomization]);

    // collect "unsupported" body part categories
    const knownBodyPartIds: string[] = useMemo(() => BODY_PART_DISPLAY_ORDER.map(bp => bp.id), []);
    const otherCategoryBodyParts = useMemo(() => {
        return stableBodyPartCategories.filter(bodyPart => !knownBodyPartIds.includes(bodyPart));
    }, [stableBodyPartCategories, knownBodyPartIds]);

    // memoize available body part tabs - use stable categories to prevent size changes
    const bodyPartTabItems = useMemo(() => {

        // always include at least the "all" tab
        const tabItems: { key: string, title: string }[] = [
            {
                key: BODY_PART_TAB_ALL,
                title: t('bodyPart.category.all')
            }
        ];

        // add body parts that we explicitly support
        for (const bp of BODY_PART_DISPLAY_ORDER) {
            if (stableBodyPartCategories.includes(bp.id)) {
                tabItems.push({
                    key: bp.id,
                    title: t(bp.translationKey),
                });
            }
        }

        // add any "other" body parts we didn't explicitly support here
        otherCategoryBodyParts.forEach(otherBodyPart => {
            tabItems.push(
                {
                    key: otherBodyPart,
                    title: capitalizeFirstCharacter(otherBodyPart)
                }
            );
        });

        return tabItems;
    }, [stableBodyPartCategories, otherCategoryBodyParts, t]);

    const filteredModels = useMemo(() => {
        // Filter by input modality compatibility with the current Image+ model
        const modalityFilteredCustomizations = contourCustomizations.filter(customization =>
            customization.inputModalities.some(modality => model.inputModalities.includes(modality))
        );

        // Apply body part and free-text filtering
        const bodyPartFilteredCustomizations = activeBodyPartTab === BODY_PART_TAB_ALL
            ? modalityFilteredCustomizations
            : modalityFilteredCustomizations.filter(customization => customization.bodyPart === activeBodyPartTab);
        const treeViewObjects = convertToTreeViewObjects(bodyPartFilteredCustomizations);
        const freeTextFiltered = filterCustomizationTreeByFreeText(treeViewObjects, filterValue);
        const result = convertFromTreeViewObjects(freeTextFiltered, bodyPartFilteredCustomizations);

        return result;
    }, [filterValue, contourCustomizations, activeBodyPartTab, model.inputModalities]);

    const initialContourCustomization = useMemo(() => {
        return allContourCustomizations.find(c => c.action === initialAction);
    },
        [initialAction, allContourCustomizations]);


    /** Reset 'scrollToModelName' after it has been scrolled to */
    const handleScrolled = useCallback(() => {
        setScrollToCustomization(undefined);
    }, []);

    const handleFilterChange = useCallback((_event: unknown, value: string) => {
        dispatch(activeFreeTextFilterSet(value));
    }, [dispatch]);

    const handleFilterClear = useCallback(() => {
        dispatch(activeFreeTextFilterReset());
    }, [dispatch]);


    return (
        <>
            <FormGroup>
                <InputGroup>
                    <InputGroupItem isFill>
                        <TextInput
                            readOnly
                            value={initialContourCustomization?.customizationLabel || ''}
                            type='text'
                            isStartTruncated
                            aria-label='Selected Contour+ model and customization for this Image+ model'
                            title={initialContourCustomization?.customizationLabel || ''}
                            data-testid="image-contour-model-selected-label"
                        />
                    </InputGroupItem>
                    <InputGroupItem>
                        <span data-testid="contour-model-change-button">
                            <Button className="contour-model-select-change-button" onClick={handleOpenModal}>Change model...</Button>
                        </span>
                    </InputGroupItem>
                </InputGroup>
            </FormGroup>

            <Modal
                variant={ModalVariant.medium}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={t('customizationPage.image.contourGenerationSection.action.modal.header')}
                actions={[
                    <Button key="confirm" variant="primary" onClick={handleConfirm} data-testid="image-contour-model-select-confirm-button">{t('customizationPage.image.contourGenerationSection.action.modal.selectModel')}</Button>,
                    <Button key="cancel" variant="secondary" onClick={handleCloseModal} data-testid="image-contour-model-select-cancel-button">{t('common.cancel')}</Button>
                ]}
                className="contour-model-select-modal"
            >
                <div data-testid="contour-model-select-modal">
                    <Split>

                        <SplitItem className="contour-model-select-nav">
                            <Tabs
                                activeKey={activeBodyPartTab}
                                onSelect={handleBodyPartTabClick}
                                isVertical
                            >
                                {bodyPartTabItems.map(tab => (
                                    <Tab
                                        className={tab.key === BODY_PART_TAB_ALL ? 'body-part-all-tab' : undefined}
                                        title={getBodyPartTitle(tab.key, tab.title)}
                                        key={tab.key}
                                        eventKey={tab.key}
                                    />
                                ))}
                            </Tabs>

                            <CustomizationFilters
                                modelType={ModelType.Contouring}
                                isInline={true}
                                hideInputModalityFilter={true}
                            />

                        </SplitItem>

                        <SplitItem isFilled>
                            <Stack>
                                <StackItem className="filter-container">
                                    <SearchInput
                                        placeholder={t('customizationPage.image.contourGenerationSection.action.modal.filter')}
                                        value={filterValue}
                                        onChange={handleFilterChange}
                                        onClear={handleFilterClear}
                                        data-testid="contour-model-search-input"
                                    />
                                </StackItem>

                                <StackItem className="model-list-container" data-testid="contour-model-list">
                                    <ModelList
                                        items={filteredModels}
                                        onItemClick={handleSelect}
                                        currentSelection={selectedContourCustomization}
                                        scrollToCustomization={scrollToCustomization}
                                        handleScrolled={handleScrolled}
                                    />
                                </StackItem>
                            </Stack>
                        </SplitItem>

                    </Split>
                </div>
            </Modal>

        </>
    );
}

export default ContourModelSelect;
